/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

export type ExtractFilterCommanderFn = (queries: Record<string, string>) => Record<string, unknown>;

export const processCommander: ExtractFilterCommanderFn = (queries) => {
  const _filters: Record<string, unknown> = {};
  Object.entries(queries).forEach(([key, val]) => {
    _filters[key] = val;
  });
  return _filters;
};

export const jobCommander: ExtractFilterCommanderFn = (queries) => {
  const _filters: Record<string, unknown> = {};
  Object.entries(queries).forEach(([key, val]) => {
    if (key === 'progress') {
      const _val = val.split(',').map((el) => parseInt(el));
      if (_val.every((el) => !isNaN(el))) {
        _filters[key] = _val;
      }
    } else {
      _filters[key] = val;
    }
  });
  return _filters;
};

export const useQueryFilters = ({
  commander,
  filter_prefix
}: {
  commander: ExtractFilterCommanderFn;
  filter_prefix?: string;
}) => {
  const [queryParams] = useSearchParams();
  const [prefilterIsLoading, setPrefilterIsLoading] = useState(true);
  const [prefilters, setPrefilters] = useState<Record<string, unknown>>({});

  useEffect(() => {
    const entries: Record<string, string> = {};
    queryParams.forEach((val, key) => {
      if (filter_prefix) {
        const [_prefix, property] = key.split('_');
        if (_prefix === filter_prefix) {
          entries[property] = val;
        }
      } else {
        entries[key] = val;
      }
    });
    const _preFilters = commander(entries);
    if (Object.keys(_preFilters).length > 0) {
      setPrefilters(_preFilters);
    }
    setPrefilterIsLoading(false);
  }, [queryParams, commander, filter_prefix]);

  return {
    queryParams,
    prefilters,
    prefilterIsLoading
  };
};
